import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Mail, Loader2, AlertCircle } from 'lucide-react';
import adminApi from '@/lib/adminApi';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await adminApi.post('/api/admin/login', credentials);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#050505] flex items-center justify-center p-6" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold tracking-tight mb-2">
            <span className="text-white">TERRY</span>
            <span className="text-[#007AFF]">WEBSTER</span>
          </h1>
          <p className="text-[#A1A1AA]">Admin Login</p>
        </div>

        <form 
          onSubmit={handleSubmit}
          className="p-8 border border-[#27272A] bg-[#0C0C0C]"
          data-testid="admin-login-form"
        >
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 text-red-400 flex items-center gap-3" data-testid="login-error-message">
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          <div className="mb-6">
            <label htmlFor="email" className="form-label">Email</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
              <input
                type="email"
                id="email"
                value={credentials.email}
                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                required
                className="w-full pl-12 pr-4 py-3 bg-[#050505] border border-[#27272A] text-white placeholder-[#52525B] focus:border-[#007AFF] focus:outline-none"
                placeholder="admin@example.com"
                data-testid="admin-email-input"
              />
            </div>
          </div>

          <div className="mb-8">
            <label htmlFor="password" className="form-label">Password</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
              <input
                type="password"
                id="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                required
                className="w-full pl-12 pr-4 py-3 bg-[#050505] border border-[#27272A] text-white placeholder-[#52525B] focus:border-[#007AFF] focus:outline-none"
                placeholder="••••••••"
                data-testid="admin-password-input"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full flex items-center justify-center gap-2"
            data-testid="admin-login-button"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Signing in...
              </>
            ) : (
              <>
                <Lock size={18} />
                Sign In
              </>
            )}
          </button>
        </form>

        <p className="text-center mt-6 text-[#52525B] text-sm">
          <a href="/" className="hover:text-[#007AFF] transition-colors">
            ← Back to website
          </a>
        </p>
      </motion.div>
    </main>
  );
}
